import { useCallback } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import {
    CpOrderInvoicesDataItem,
    CpOrderInvoicesListStoreHook,
    CpOrderInvoicesListAction,
} from '../cp_order_invoices_list_types';
import { cpOrderInvoicesDataAtom, cpOrderInvoicesPageStateAtom } from '../cp_order_invoices_list_atoms';
import cpOrderInvoicesRepo from '../../data/cpOrderInvoicesRepo';
import { tableDataVM } from '../../../../core/table/table_view_models';
import { CpOrderInvoicesSearchModel } from '../search/cp_order_invoices_search_types';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { getDefaultStore } from 'jotai';
import { cpOrderInvoicesSearchFormAtom } from '../search/cp_order_invoices_search_atoms';
import { cpOrderInvoicesDetailsPageStateAtom } from '../../detail/cp_order_invoices_details_atoms';
import { useRouter } from '@uirouter/react';
import { CP_ORDER_INVOICES_ROUTE_DETAILS } from '../../cp_order_invoices_const';

type CpOrderInvoicesStoreActions = Pick<
    CpOrderInvoicesListStoreHook,
    'getList' | 'setAction' | 'navigateToDetailsPage'
>;

const hasValidFacilityAndPharmacy = (model: CpOrderInvoicesSearchModel): boolean => {
    if (
        ((model.CE?.val === 'N/A' || model.CE?.val === '') &&
            (model.ProgramAdmin?.val === 'N/A' || model.ProgramAdmin?.val === '')) ||
        model.RX?.val?.length === 0
    ) {
        console.warn('Invalid params', {
            CE: model.CE?.val,
            ProgramAdmin: model.ProgramAdmin?.val,
        });
        return false;
    }
    return true;
};

const useCpOrderInvoicesStoreActionsCore = (): CpOrderInvoicesStoreActions => {
    const router = useRouter();
    const setState = useSetImmerAtom(cpOrderInvoicesPageStateAtom);
    const setList = useSetImmerAtom(cpOrderInvoicesDataAtom);
    const setDetailsPageState = useSetImmerAtom(cpOrderInvoicesDetailsPageStateAtom);

    const getListAsync = useCallback(
        async (query: CpOrderInvoicesSearchModel) => {
            try {
                ngDelegate.wait(true, true);
                setState(draft => {
                    draft.loading = true;
                    draft.action = 'QUERY';
                    return draft;
                });
                const res = await cpOrderInvoicesRepo.getList(query);

                // Client-side filter: exclude rows without InvoiceReconciledDate when ReconciledOnly is checked
                let items = res as CpOrderInvoicesDataItem[];
                if (query.ReconciledOnly?.val) {
                    items = items.filter(item => !!item.InvoiceReconciledDate);
                }

                // success, set table data and switch to LIST mode
                setState(draft => {
                    draft.action = 'LIST';
                    return draft;
                });
                setList(() => {
                    const vm = tableDataVM<CpOrderInvoicesDataItem>(items);
                    return vm;
                });
            } catch (err: any) {
                setList(() => tableDataVM<CpOrderInvoicesDataItem>());
            } finally {
                ngDelegate.wait(false);
                setState(draft => {
                    draft.loading = false;
                    return draft;
                });
            }
        },
        [setState, setList],
    );

    const setAction = useCallback(
        (action: CpOrderInvoicesListAction): void => {
            setState(draft => {
                draft.action = action;
            });
        },
        [setState],
    );

    const getList = useCallback(
        (query?: CpOrderInvoicesSearchModel): void => {
            // Read current form state on demand to avoid subscribing this hook to every form edit.
            const model = query ?? getDefaultStore().get(cpOrderInvoicesSearchFormAtom);
            if (hasValidFacilityAndPharmacy(model)) {
                getListAsync(model);
            } else {
                // If for any reason the request params are invalid,
                // set the feature back to its form view.
                setAction('QUERY');
            }
        },
        [getListAsync, setAction],
    );

    const navigateToDetailsPage = useCallback(
        (rowItem: CpOrderInvoicesDataItem, rebatesOnly: boolean = false): void => {
            setDetailsPageState(draft => {
                draft.id = rowItem.Id;
                draft.title = 'CP Invoice Detail';
                draft.rebatesOnly = rebatesOnly;
            });
            router.stateService.go(CP_ORDER_INVOICES_ROUTE_DETAILS, { Id: rowItem.Id });
        },
        [router.stateService, setDetailsPageState],
    );

    return { getList, setAction, navigateToDetailsPage };
};

export const useCpOrderInvoicesStoreActions = (): CpOrderInvoicesStoreActions => {
    return useCpOrderInvoicesStoreActionsCore();
};

export const useCpOrderInvoicesStore = (): CpOrderInvoicesListStoreHook => {
    const [state] = useImmerAtom(cpOrderInvoicesPageStateAtom);
    const { getList, setAction, navigateToDetailsPage } = useCpOrderInvoicesStoreActionsCore();

    return {
        action: state.action,
        loading: state.loading,
        userType: state.userType,
        userAffList: state.userAffList,
        userImpersonate: state.userImpersonate,
        getList,
        setAction,
        navigateToDetailsPage,
    };
};
